import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { Hand, Palette, ListChecks, Plus, RefreshCw, PiggyBank, Users, PartyPopper } from "lucide-react";

const TEXTS = {
  es: {
    welcome_title:    "¡Bienvenido a ARIA Wallet!",
    welcome_body:     "Te guiamos en tus primeros pasos. Puedes interactuar con la app en cada paso.",
    welcome_btn:      "Empezar",
    skip:             "Saltar",
    next:             "Siguiente →",
    finish:           "¡Empezar a usar ARIA Wallet!",
    step:             "Paso",
    of:               "de",
    theme_title:      "Elige tu tema",
    theme_body:       "Toca cualquier tema para cambiarlo al instante.",
    cats_title:       "Personaliza tus categorías",
    cats_body:        "Escribe tus categorías una por línea. Luego pulsa 'Guardar cambios'.",
    entry_title:      "Añade tu primera entrada",
    entry_body:       "Elige tipo, categoría, importe y fecha. Luego pulsa 'Añadir entrada'.",
    recurring_title:  "Entradas recurrentes",
    recurring_body:   "Configura gastos/ingresos fijos mensuales. Se añaden solos el día 1.",
    savings_title:    "Huchas de ahorro",
    savings_body:     "Crea metas de ahorro y sigue tu progreso. Perfectas para cualquier objetivo.",
    shared_title:     "Cuenta compartida",
    shared_body:      "Comparte tus finanzas con tu pareja o socio. Se sincroniza en tiempo real.",
    done_title:       "¡Ya estás listo!",
    done_body:        "También tienes Pendientes, Presupuestos y Gráficos esperándote.",
  },
  en: {
    welcome_title:    "Welcome to ARIA Wallet!",
    welcome_body:     "We'll guide you through your first steps. You can interact with the app at each step.",
    welcome_btn:      "Start",
    skip:             "Skip",
    next:             "Next →",
    finish:           "Start using ARIA Wallet!",
    step:             "Step",
    of:               "of",
    theme_title:      "Choose your theme",
    theme_body:       "Tap any theme to change it instantly.",
    cats_title:       "Customize your categories",
    cats_body:        "Write your categories one per line. Then tap 'Save changes'.",
    entry_title:      "Add your first entry",
    entry_body:       "Choose type, category, amount and date. Then tap 'Add entry'.",
    recurring_title:  "Recurring entries",
    recurring_body:   "Set up fixed monthly income/expenses. Added automatically on the 1st.",
    savings_title:    "Savings jars",
    savings_body:     "Create savings goals and track progress. Perfect for any target.",
    shared_title:     "Shared account",
    shared_body:      "Share finances with your partner or associate. Syncs in real time.",
    done_title:       "You're all set!",
    done_body:        "You also have Pending payments, Budgets and Charts waiting for you.",
  },
};

// useAnchor: true = posicionamiento dinámico junto al elemento (Ajustes)
// useAnchor: false = tooltip compacto fijo arriba (páginas completas)
const STEPS = [
  { id: "welcome",   path: null,                anchor: null,               useAnchor: false, pos: "center",  icon: Hand },
  { id: "theme",     path: "/ajustes",           anchor: "onboarding-theme", useAnchor: true,  pos: "dynamic", icon: Palette },
  { id: "cats",      path: "/ajustes",           anchor: "onboarding-cats",  useAnchor: true,  pos: "dynamic", icon: ListChecks },
  { id: "entry",     path: "/anadir",            anchor: null,               useAnchor: false, pos: "top",     icon: Plus },
  { id: "recurring", path: "/recurrentes",       anchor: null,               useAnchor: false, pos: "bottom",  icon: RefreshCw },
  { id: "savings",   path: "/huchas",            anchor: null,               useAnchor: false, pos: "bottom",  icon: PiggyBank },
  { id: "done",      path: null,                 anchor: null,               useAnchor: false, pos: "center",  icon: PartyPopper },
];
const TOTAL = STEPS.length;

function detectLang(profile) {
  if (profile?.lang) return profile.lang === "en" ? "en" : "es";
  return (navigator.language || "es").toLowerCase().startsWith("en") ? "en" : "es";
}

function getAnchorPos(anchorId) {
  const el = document.getElementById(anchorId);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  return {
    top:    rect.top,
    bottom: rect.bottom,
    centerX: rect.left + rect.width / 2,
    isInBottomHalf: (rect.top + rect.height / 2) > window.innerHeight / 2,
  };
}

export default function OnboardingTour() {
  const { user, profile } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [step,    setStep]    = useState(0);
  const [visible, setVisible] = useState(false);
  const [lang,    setLang]    = useState("es");
  const [anchorPos, setAnchorPos] = useState(null);
  const highlightRef = useRef(null);
  const checkedRef   = useRef(false); // evita re-ejecuciones en cada update de profile

  useEffect(() => {
    if (!user || !profile) return;
    if (checkedRef.current) return; // ya se comprobó, no volver a ejecutar
    checkedRef.current = true;
    if (profile.onboardingDone) return; // ya completó el tour, nada que hacer
    setLang(detectLang(profile));
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, [user, profile]);

  useEffect(() => {
    if (!visible) return;
    const current = STEPS[step];
    if (current.path && location.pathname !== current.path) navigate(current.path);
  }, [step, visible]);

  useEffect(() => {
    if (!visible) return;
    const current = STEPS[step];

    // Quitar resaltado anterior
    if (highlightRef.current) {
      highlightRef.current.classList.remove("aria-tour-hl");
      highlightRef.current = null;
    }
    setAnchorPos(null);

    if (!current.anchor) return;

    const t = setTimeout(() => {
      // Buscar por ID primero; si no existe, usar el page-header de la página
      let el = document.getElementById(current.anchor);
      if (!el) {
        el = document.querySelector(".page-header") || document.querySelector(".page");
        if (el && !el.id) el.id = current.anchor; // asignar ID para getAnchorPos
      }
      if (!el) return;
      el.classList.add("aria-tour-hl");
      highlightRef.current = el;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => setAnchorPos(getAnchorPos(el.id || current.anchor)), 450);
    }, 500);
    return () => clearTimeout(t);
  }, [step, location.pathname, visible]);

  useEffect(() => {
    if (!visible) return;
    const current = STEPS[step];
    if (!current.anchor) return;
    const onScroll = () => setAnchorPos(getAnchorPos(current.anchor));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [step, visible]);

  useEffect(() => () => {
    if (highlightRef.current) highlightRef.current.classList.remove("aria-tour-hl");
  }, []);

  const T = useCallback((key) => TEXTS[lang]?.[key] || TEXTS.es[key], [lang]);

  const markDone = useCallback(async () => {
    if (!user) return;
    await setDoc(doc(db, "users", user.uid), { onboardingDone: true }, { merge: true });
  }, [user]);

  const clearHighlight = useCallback(() => {
    if (highlightRef.current) {
      highlightRef.current.classList.remove("aria-tour-hl");
      highlightRef.current = null;
    }
    setAnchorPos(null);
  }, []);

  async function handleNext() {
    clearHighlight();
    if (step >= TOTAL - 1) {
      await markDone(); setVisible(false); navigate("/panel");
    } else {
      setStep(s => s + 1);
    }
  }

  async function handleSkip() {
    clearHighlight();
    await markDone(); setVisible(false); navigate("/panel");
  }

  if (!visible) return null;

  const current   = STEPS[step];
  const isWelcome = current.id === "welcome";
  const isDone    = current.id === "done";
  const isFullPage = !current.useAnchor && !isWelcome && !isDone;

  // ── Calcular posición del tooltip ─────────────────────────────────────────
  const TOOLTIP_W = Math.min(window.innerWidth * 0.94, 380);
  const ARROW     = 10;
  const GAP       = 10;
  let   wrapStyle = {};
  let   arrowStyle = null;

  if (isWelcome || isDone) {
    // Centrado en pantalla
    wrapStyle = {
      position: "fixed", top: "50%", left: "50%",
      transform: "translate(-50%, -50%)",
      width: TOOLTIP_W,
    };
  } else if (isFullPage) {
    // Posición según la propiedad 'pos' del paso:
    // "top"    → fijo arriba (entry: el botón guardar está abajo, no se tapa)
    // "bottom" → fijo abajo sobre la barra de navegación (recurring, savings)
    if (current.pos === "bottom") {
      wrapStyle = {
        position: "fixed", bottom: 72, left: "50%",
        transform: "translateX(-50%)",
        width: TOOLTIP_W,
      };
    } else {
      wrapStyle = {
        position: "fixed", top: 60, left: "50%",
        transform: "translateX(-50%)",
        width: TOOLTIP_W,
      };
    }
  } else if (anchorPos) {
    // Dinámico junto al elemento (Ajustes)
    let leftPos = anchorPos.centerX - TOOLTIP_W / 2;
    leftPos = Math.max(8, Math.min(leftPos, window.innerWidth - TOOLTIP_W - 8));
    const arrowLeft = Math.max(16, Math.min(anchorPos.centerX - leftPos - ARROW, TOOLTIP_W - 32));

    if (anchorPos.isInBottomHalf) {
      wrapStyle = {
        position: "fixed",
        bottom: window.innerHeight - anchorPos.top + GAP + ARROW,
        left: leftPos, width: TOOLTIP_W,
      };
      arrowStyle = {
        position: "absolute", bottom: -ARROW, left: arrowLeft,
        width: 0, height: 0,
        borderLeft: `${ARROW}px solid transparent`,
        borderRight: `${ARROW}px solid transparent`,
        borderTop: `${ARROW}px solid #D4AF37`,
      };
    } else {
      wrapStyle = {
        position: "fixed",
        top: anchorPos.bottom + GAP + ARROW,
        left: leftPos, width: TOOLTIP_W,
      };
      arrowStyle = {
        position: "absolute", top: -ARROW, left: arrowLeft,
        width: 0, height: 0,
        borderLeft: `${ARROW}px solid transparent`,
        borderRight: `${ARROW}px solid transparent`,
        borderBottom: `${ARROW}px solid #D4AF37`,
      };
    }
  } else {
    // Fallback mientras calcula
    wrapStyle = {
      position: "fixed", bottom: 80, left: "50%",
      transform: "translateX(-50%)", width: TOOLTIP_W,
    };
  }

  const isCompact = isFullPage;

  return (
    <>
      <style>{`
        @keyframes ariaTourIn {
          from { opacity:0; transform: translateY(8px); }
          to   { opacity:1; transform: translateY(0); }
        }
        .aria-tour-hl {
          outline: 2.5px solid #D4AF37 !important;
          outline-offset: 5px !important;
          border-radius: 12px !important;
          animation: ariaTourPulse 1.8s ease-in-out infinite !important;
        }
        @keyframes ariaTourPulse {
          0%,100% { box-shadow: 0 0 0 0 rgba(212,175,55,0.5); }
          50%      { box-shadow: 0 0 0 8px rgba(212,175,55,0); }
        }
      `}</style>

      {(isWelcome || isDone) && (
        <div style={{
          position:"fixed", inset:0, zIndex:9998,
          background:"rgba(0,0,0,0.6)", backdropFilter:"blur(2px)",
        }} />
      )}

      <div style={{
        ...wrapStyle,
        zIndex: 9999,
        background: "#0a0a0a",
        border: "1.5px solid #D4AF37",
        borderRadius: 14,
        padding: isCompact ? "10px 14px 10px" : "16px 16px 13px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.7)",
        animation: "ariaTourIn 0.22s ease",
      }}>
        {arrowStyle && <div style={arrowStyle} />}

        {/* Progreso — en línea para modo compacto */}
        {!isWelcome && (
          <div style={{
            display:"flex", justifyContent:"space-between",
            alignItems:"center", marginBottom: isCompact ? 6 : 10,
          }}>
            <div style={{ flex:1 }}>
              <div style={{
                display:"flex", justifyContent:"space-between",
                fontSize:11, color:"#777", marginBottom: isCompact ? 3 : 5,
              }}>
                <span style={{ color:"#D4AF37", fontWeight:600 }}>
                  {T("step")} {step} {T("of")} {TOTAL - 1}
                </span>
                <button onClick={handleSkip} style={{
                  background:"none", border:"none", color:"#666",
                  cursor:"pointer", fontSize:11, padding:0, textDecoration:"underline",
                }}>{T("skip")}</button>
              </div>
              <div style={{ height:3, background:"#222", borderRadius:2 }}>
                <div style={{
                  height:"100%", borderRadius:2,
                  background:"linear-gradient(90deg,#D4AF37,#F0C040)",
                  width:`${(step/(TOTAL-1))*100}%`,
                  transition:"width 0.35s ease",
                }} />
              </div>
            </div>
          </div>
        )}

        {/* En modo compacto: título + texto en la misma fila que el botón */}
        {isCompact ? (
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <p style={{ fontSize:13, fontWeight:800, color:"#D4AF37", margin:0, lineHeight:1.2, display:"flex", alignItems:"center", gap:6 }}>
                <current.icon size={15} style={{flexShrink:0}} />
                {T(`${current.id}_title`)}
              </p>
              <p style={{ fontSize:11.5, color:"#aaa", margin:"3px 0 0", lineHeight:1.4 }}>
                {T(`${current.id}_body`)}
              </p>
            </div>
            <button onClick={handleNext} style={{
              padding:"7px 14px", borderRadius:8, fontSize:12, fontWeight:700,
              background:"linear-gradient(135deg,#D4AF37,#F0C040)",
              border:"none", color:"#000", cursor:"pointer",
              whiteSpace:"nowrap", flexShrink:0,
            }}>
              {T("next")}
            </button>
          </div>
        ) : (
          <>
            <h3 style={{
              fontSize: isWelcome ? 17 : 15, fontWeight:800,
              color:"#D4AF37", margin:"0 0 6px", lineHeight:1.3,
              display:"flex", alignItems:"center", gap:8,
            }}>
              <current.icon size={isWelcome ? 19 : 17} style={{flexShrink:0}} />
              {T(`${current.id}_title`)}
            </h3>
            <p style={{ fontSize:13, color:"#bbb", lineHeight:1.6, margin:"0 0 12px" }}>
              {T(`${current.id}_body`)}
            </p>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              {isWelcome && (
                <button onClick={handleSkip} style={{
                  padding:"7px 13px", borderRadius:8, fontSize:12,
                  background:"none", border:"1px solid #333", color:"#777", cursor:"pointer",
                }}>
                  {T("skip")}
                </button>
              )}
              <button onClick={handleNext} style={{
                padding:"7px 18px", borderRadius:8, fontSize:13, fontWeight:700,
                background:"linear-gradient(135deg,#D4AF37,#F0C040)",
                border:"none", color:"#000", cursor:"pointer",
                minWidth:100, textAlign:"center",
              }}>
                {isDone ? T("finish") : isWelcome ? T("welcome_btn") : T("next")}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
